import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { fetchPlayoffStatus, startPlayoffRun, generatePlayoffBracket } from '../../api/playoff';
import { startGame, startGameWithSubs } from '../../api/games';
import { fetchUserTeams } from '../../api/userTeams';
import { filterSelectableUserTeams } from '../../helpers/teamHelpers';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/LoadingSpinner';
import FatiguedSubstitutionModal from '../../components/FatiguedSubstitutionModal';
import GLOBALS from '../../config/globals';
import { getSeriesAndRound, getPlayoffTrophyTier } from '../../helpers/playoffHelpers';
import { handleActiveGameExists } from '../../helpers/startGameConflicts';

const { PLAYOFF_ROUND_NAMES, PLAYOFF_SERIES_NAMES, PLAYOFF_GAMES_PER_SERIES, PLAYOFF_SERIES_REWARDS } = GLOBALS;

const PlayoffPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [bracket, setBracket] = useState(null);
    const [run, setRun] = useState(null);
    const [activeGameID, setActiveGameID] = useState(null);
    const [games, setGames] = useState([]);
    const [teams, setTeams] = useState([]);
    const [selectedTeamID, setSelectedTeamID] = useState('');
    const [starting, setStarting] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [showSubModal, setShowSubModal] = useState(false);
    const [fatiguedData, setFatiguedData] = useState(null);
    const [pendingPayload, setPendingPayload] = useState(null);

    const loadStatus = useCallback(async () => {
        try {
            const status = await fetchPlayoffStatus();
            setBracket(status.bracket);
            setRun(status.run);
            setActiveGameID(status.activeGameID ?? null);
            setGames(status.games ?? []);
        } catch (error) {
            console.error('Failed to load playoff status:', error);
            toast.error('Failed to load playoff status');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    useEffect(() => {
        let cancelled = false;
        if (!run && currentUser) {
            (async () => {
                try {
                    const data = await fetchUserTeams();
                    if (cancelled) return;
                    const userTeams = filterSelectableUserTeams(data);
                    setTeams(userTeams);
                    if (userTeams.length > 0) setSelectedTeamID(String(userTeams[0].userTeamID));
                } catch (error) {
                    if (!cancelled) toast.error('Failed to load teams');
                }
            })();
        }
        return () => { cancelled = true; };
    }, [run, currentUser]);

    const handleStart = async () => {
        if (!selectedTeamID) return;
        setStarting(true);
        try {
            await startPlayoffRun(selectedTeamID);
            await loadStatus();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to start run');
        } finally {
            setStarting(false);
        }
    };

    const handlePlay = async () => {
        if (activeGameID) {
            navigate(`/game/${activeGameID}/play`);
            return;
        }
        setPlaying(true);
        try {
            const newGame = await startGame({ playoffGame: true });
            navigate(`/game/${newGame.gameID}/play`);
        } catch (error) {
            if (handleActiveGameExists(error, navigate)) return;
            if (error.response?.status === 409 && error.response?.data?.code === 'PLAYERS_FATIGUED') {
                setFatiguedData({
                    unavailableCards: error.response.data.unavailableCards,
                    availableSubstitutes: error.response.data.availableSubstitutes,
                });
                setPendingPayload(error.response.data.pendingPayload);
                setShowSubModal(true);
            } else if (error.response?.data?.gameID) {
                navigate(`/game/${error.response.data.gameID}/play`);
            } else {
                toast.error(error.response?.data?.error || 'Failed to start game');
            }
        } finally {
            setPlaying(false);
        }
    };

    const handleSubConfirm = async (subs) => {
        setShowSubModal(false);
        setPlaying(true);
        try {
            const newGame = await startGameWithSubs({ ...pendingPayload, substitutions: subs });
            navigate(`/game/${newGame.gameID}/play`);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to start game after substitutions');
        } finally {
            setPlaying(false);
            setFatiguedData(null);
            setPendingPayload(null);
        }
    };

    const handleSubCancel = () => {
        setShowSubModal(false);
        setFatiguedData(null);
        setPendingPayload(null);
    };

    const handleAllHealed = async () => {
        setShowSubModal(false);
        setFatiguedData(null);
        setPendingPayload(null);
        await handlePlay();
    };

    if (loading) return <div className="playoff-container"><LoadingSpinner /></div>;

    if (!bracket) {
        return (
            <div className="playoff-container">
                <div className="banner-header"><h1>Daily Postseason Push</h1></div>
                <div className="playoff-empty">No bracket available today. Check back tomorrow!</div>
            </div>
        );
    }

    const rounds = bracket.rounds || [];
    const isEliminated = run?.status === GLOBALS.PLAYOFF_STATUS.ELIMINATED;
    const isChampion = run?.status === GLOBALS.PLAYOFF_STATUS.CHAMPION;

    // Group rounds into series of 4
    const series = [1, 2, 3].map(seriesIdx => {
        const start = (seriesIdx - 1) * PLAYOFF_GAMES_PER_SERIES;
        return {
            seriesIndex: seriesIdx,
            seriesName: PLAYOFF_SERIES_NAMES[seriesIdx],
            rounds: rounds.slice(start, start + PLAYOFF_GAMES_PER_SERIES),
        };
    });

    // A series is locked if the user hasn't reached it yet
    const currentSeriesIndex = run ? Math.ceil(run.currentRound / PLAYOFF_GAMES_PER_SERIES) : 0;

    const trophyInfo = run ? getPlayoffTrophyTier(run.currentRound, run.status) : null;

    return (
        <div className="playoff-container">
            <div className="banner-header">
                <h1>Daily Postseason Push</h1>
                {run ? (
                    <span className="playoff-team-name">{run.UserTeam?.name || 'Your Team'}</span>
                ) : null}
                {currentUser?.isAdmin ? (
                    <button
                        className="boring_button"
                        onClick={async () => {
                            if (!window.confirm('Generate today\'s playoff bracket? This won\'t affect existing runs.')) return;
                            try {
                                const result = await generatePlayoffBracket();
                                toast.success(result.created ? 'Bracket generated' : 'Bracket already exists');
                                await loadStatus();
                            } catch (e) {
                                toast.error(e.response?.data?.error || 'Failed to generate bracket');
                            }
                        }}
                    >
                        Generate Bracket
                    </button>
                ) : null}
            </div>

            {!run ? (
                <div className="playoff-start">
                    <p>Win up to 12 games across 3 series. One loss and you're done for the day.</p>
                    <div className="playoff-team-select">
                        <label htmlFor="playoff-team">Choose your team:</label>
                        <select
                            id="playoff-team"
                            value={selectedTeamID}
                            onChange={e => setSelectedTeamID(e.target.value)}
                        >
                            {teams.map(team => (
                                <option key={team.userTeamID} value={String(team.userTeamID)}>
                                    {team.name} ({team.score})
                                </option>
                            ))}
                        </select>
                        <button onClick={handleStart} disabled={starting || !selectedTeamID}>
                            {starting ? 'Starting...' : 'Start Run'}
                        </button>
                    </div>
                </div>
            ) : null}

            {series.map(({ seriesIndex, seriesName, rounds: seriesRounds }) => {
                const isSeriesLocked = !run || currentSeriesIndex < seriesIndex;
                const isSeriesComplete = run && (
                    currentSeriesIndex > seriesIndex ||
                    (isChampion && currentSeriesIndex === seriesIndex)
                );

                return (
                    <div key={seriesIndex} className={`playoff-series ${isSeriesLocked ? 'locked' : ''}`}>
                        <div className="playoff-series-header">
                            <span className="playoff-series-name">{seriesName}</span>
                            {isSeriesComplete ? <span className="playoff-series-swept">Swept</span> : null}
                        </div>
                        <div className="playoff-bracket">
                            {seriesRounds.map((roundData, i) => {
                                const roundNum = roundData.round;
                                const gameInSeries = roundData.gameInSeries || (i + 1);
                                const rewardText = PLAYOFF_SERIES_REWARDS[seriesIndex]?.[gameInSeries] || '';
                                const isCompleted = run && (run.currentRound > roundNum || (isChampion && run.currentRound === roundNum));
                                const isCurrent = run && run.status === GLOBALS.PLAYOFF_STATUS.ACTIVE && run.currentRound === roundNum;
                                const isLossRound = isEliminated && run.currentRound === roundNum;
                                const won = isCompleted && !isLossRound;
                                const game = games[roundNum - 1];
                                const scoreMatch = game?.resultDescription?.match(/H:\s*(\d+)\s*-\s*V:\s*(\d+)/);
                                const score = scoreMatch ? `${scoreMatch[2]}-${scoreMatch[1]}` : null;

                                return (
                                    <React.Fragment key={roundNum}>
                                        {i > 0 ? <div className="playoff-connector">{won || isCompleted ? '→' : '·'}</div> : null}
                                        <div className={`playoff-round ${won ? 'won' : ''} ${isLossRound ? 'lost' : ''} ${isCurrent ? 'current' : ''} ${!run || roundNum > run.currentRound ? 'locked' : ''}`}>
                                            <div className="playoff-round-header">
                                                <span className="playoff-round-name">{roundData.gameRoundName || PLAYOFF_ROUND_NAMES[gameInSeries]}</span>
                                                <span className="playoff-round-reward">{rewardText}</span>
                                            </div>
                                            <div className="playoff-round-opponent">
                                                {(isCompleted || isCurrent || isLossRound || gameInSeries === 1) && roundData.opponent ? (
                                                    <span className="playoff-opponent-name">
                                                        {roundData.opponent.name} <span className="playoff-opponent-score">({roundData.opponent.score})</span>
                                                    </span>
                                                ) : (
                                                    <span className="playoff-opponent-name">???</span>
                                                )}
                                            </div>
                                            <div className="playoff-round-status">
                                                {won ? (
                                                    game?.gameID ? (
                                                        <Link to={`/game/${game.gameID}/summary`} className="playoff-game-result playoff-game-win">
                                                            <span className="playoff-win">W</span>
                                                            {score ? <span className="playoff-score">{score}</span> : null}
                                                        </Link>
                                                    ) : (
                                                        <div className="playoff-game-result playoff-game-win">
                                                            <span className="playoff-win">W</span>
                                                        </div>
                                                    )
                                                ) : null}
                                                {isLossRound ? (
                                                    game?.gameID ? (
                                                        <Link to={`/game/${game.gameID}/summary`} className="playoff-game-result playoff-game-loss">
                                                            <span className="playoff-loss">L</span>
                                                            {score ? <span className="playoff-score">{score}</span> : null}
                                                        </Link>
                                                    ) : (
                                                        <div className="playoff-game-result playoff-game-loss">
                                                            <span className="playoff-loss">L</span>
                                                        </div>
                                                    )
                                                ) : null}
                                                {isCurrent && !activeGameID ? (
                                                    <button className="less-action" onClick={() => handlePlay()} disabled={playing}>
                                                        {playing ? 'Starting...' : 'Play'}
                                                    </button>
                                                ) : null}
                                                {isCurrent && activeGameID ? (
                                                    <Link to={`/game/${activeGameID}/play`} className="playoff-game-result playoff-game-active">
                                                        <span className="playoff-score-label">In Progress</span>
                                                        {score ? <span className="playoff-score">{score}</span> : null}
                                                    </Link>
                                                ) : null}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {isChampion ? (
                <div className="playoff-status playoff-champion">
                    <img src="/img/trophies/playoff_win.png" alt="Champion" className="playoff-champion-trophy playoff-trophy-gold" />
                    <span>Champion! You swept all 12 games. Gold trophy earned!</span>
                </div>
            ) : null}
            {isEliminated ? (
                <div className={`playoff-status ${trophyInfo ? 'playoff-trophy-earned' : 'playoff-eliminated'}`}>
                    {trophyInfo ? (
                        <>
                            <img src="/img/trophies/playoff_win.png" alt={trophyInfo.label} className={`playoff-champion-trophy playoff-trophy-${trophyInfo.tier}`} />
                            <span>Eliminated in {getSeriesAndRound(run.currentRound)}. {trophyInfo.label} earned!</span>
                        </>
                    ) : (
                        <span>Eliminated in {getSeriesAndRound(run.currentRound)}. Try again tomorrow!</span>
                    )}
                </div>
            ) : null}

            {showSubModal && fatiguedData ? (
                <FatiguedSubstitutionModal
                    unavailableCards={fatiguedData.unavailableCards}
                    availableSubstitutes={fatiguedData.availableSubstitutes}
                    onConfirm={handleSubConfirm}
                    onCancel={handleSubCancel}
                    onAllHealed={handleAllHealed}
                    title="Some Players Need Rest"
                />
            ) : null}
        </div>
    );
};

export default PlayoffPage;
